import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FinancialYearProvider } from "./context/FinancialYearContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ExpensesPage from "./pages/ExpensesPage";
import ExpenseDetailPage from "./pages/ExpenseDetailPage";
import ExpenseFormPage from "./pages/ExpenseFormPage";
import LedgerPage from "./pages/LedgerPage";
import ReportsPage from "./pages/ReportsPage";
import PnLPage from "./pages/PnLPage";
import PnLReportPage from "./pages/PnLReportPage";
import CrmProjectsPage from "./pages/CrmProjectsPage";
import HistoricalDataPage from "./pages/HistoricalDataPage";
import InvoicesPage from "./pages/InvoicesPage";
import EstimatesPage from "./pages/EstimatesPage";
import FunderPage from "./pages/FunderPage";
import FunderPayoutPage from "./pages/FunderPayoutPage";
import FunderGate from "./components/FunderGate";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import WebhookLogsPage from "./pages/WebhookLogsPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import QuoteCalculatorPage from "./pages/QuoteCalculatorPage";

// Components
import Layout from "./components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-950"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-950"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <ExpensesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/new"
        element={
          <ProtectedRoute>
            <ExpenseFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/:id"
        element={
          <ProtectedRoute>
            <ExpenseDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/:id/edit"
        element={
          <ProtectedRoute>
            <ExpenseFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger"
        element={
          <ProtectedRoute>
            <LedgerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pnl"
        element={
          <ProtectedRoute>
            <PnLPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pnl-report"
        element={
          <ProtectedRoute>
            <PnLReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm-projects"
        element={
          <ProtectedRoute>
            <CrmProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/historical-data"
        element={
          <ProtectedRoute>
            <HistoricalDataPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/estimates"
        element={
          <ProtectedRoute>
            <EstimatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/funder"
        element={
          <ProtectedRoute>
            <FunderGate>
              <FunderPage />
            </FunderGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/funder/payout"
        element={
          <ProtectedRoute>
            <FunderGate>
              <FunderPayoutPage />
            </FunderGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/webhooks"
        element={
          <ProtectedRoute>
            <WebhookLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute>
            <AuditLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Public Quote Calculator (no login) */}
      <Route path="/quote-calculator" element={<QuoteCalculatorPage />} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FinancialYearProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </FinancialYearProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
